import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@sg/garnish';

import { useFavoriteLineItemMutation } from '../../graphql/OrderStatus.generated';

export const useFavoriteLineItem = () => {
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();

  const [{ fetching: isFavoringLineItem }, favoriteLineItem] = useFavoriteLineItemMutation();

  const handleFavoriteLineItem = useCallback(
    async ({ lineItemId, productId, favorited }: ToggleFavoriteType) => {
      if (!productId || !lineItemId) return;

      const response = await favoriteLineItem({
        input: {
          favorited,
          lineItemId,
          productId,
        },
      });

      const result = response.data?.favoriteLineItem;
      const hasError = response.error || result?.__typename !== 'LineItem';

      if (hasError) {
        addNoticeBanner({
          palette: 'caution',
          text: formatMessage(messages.error),
        });
      }
    },
    [addNoticeBanner, favoriteLineItem, formatMessage],
  );

  return { isFavoringLineItem, handleFavoriteLineItem };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  error: {
    defaultMessage: 'Something went wrong, please try again later.',
    description: 'Order Status | Favorite | Error',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ToggleFavoriteType = {
  lineItemId?: string;
  productId?: string;
  favorited: boolean;
};
